import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Snackbar,
} from '@mui/material';
import { Add, Save, Cancel } from '@mui/icons-material';
import axios from 'axios';

const EditJob = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [skills, setSkills] = useState([]);
  const [currentSkill, setCurrentSkill] = useState('');
  const [isRollingApplications, setIsRollingApplications] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    department: '',
    location: '',
    experienceLevel: 'entry',
    jobType: 'full-time',
    workMode: 'onsite',
    salaryMin: '',
    salaryMax: '',
    applicationDeadline: '',
    openings: 1,
    responsibilities: '',
    requirements: '',
    benefits: '',
    status: 'active'
  });

  useEffect(() => {
    fetchJob();
  }, [id]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`http://localhost:5000/api/jobs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const job = response.data.data;
      
      // Parse the job data
      setFormData({
        title: job.title || '',
        description: job.description || '',
        department: job.department || '',
        location: job.location || '',
        experienceLevel: job.experienceLevel || 'entry',
        jobType: job.jobType || 'full-time',
        workMode: job.workMode || 'onsite',
        salaryMin: job.salaryRange?.min || '',
        salaryMax: job.salaryRange?.max || '',
        applicationDeadline: job.applicationDeadline ? 
          new Date(job.applicationDeadline).toISOString().split('T')[0] : '',
        openings: job.openings || 1,
        responsibilities: job.responsibilities ? job.responsibilities.join('\n') : '',
        requirements: job.requirements?.education || '',
        benefits: job.benefits ? job.benefits.join('\n') : '',
        status: job.status || 'active'
      });
      
      setSkills(job.requirements?.skills || []);
      
      // Check if it's rolling applications (deadline > 1 year from now)
      const deadline = new Date(job.applicationDeadline);
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      setIsRollingApplications(deadline > oneYearFromNow);
      
    } catch (err) {
      console.error('Error fetching job:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleAddSkill = () => {
    if (currentSkill.trim() && !skills.includes(currentSkill.trim())) {
      setSkills([...skills, currentSkill.trim()]);
      setCurrentSkill('');
    }
  };

  const handleDeleteSkill = (skillToDelete) => {
    setSkills(skills.filter(skill => skill !== skillToDelete));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Prepare the job data
      const jobData = {
        title: formData.title,
        description: formData.description,
        department: formData.department,
        location: formData.location,
        experienceLevel: formData.experienceLevel,
        jobType: formData.jobType,
        workMode: formData.workMode,
        applicationDeadline: isRollingApplications 
          ? new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toISOString()
          : formData.applicationDeadline,
        openings: parseInt(formData.openings),
        requirements: {
          skills: skills,
          education: formData.requirements
        },
        responsibilities: formData.responsibilities.split('\n').filter(r => r.trim()),
        benefits: formData.benefits.split('\n').filter(b => b.trim()),
        status: formData.status
      };

      // Only add salary if provided
      if (formData.salaryMin && formData.salaryMax) {
        jobData.salaryRange = {
          min: parseInt(formData.salaryMin),
          max: parseInt(formData.salaryMax),
          currency: 'INR'
        };
      }

      await axios.put(
        `http://localhost:5000/api/jobs/${id}`,
        jobData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(true);
      setTimeout(() => {
        navigate('/manage-jobs');
      }, 2000);
    } catch (err) {
      console.error('Error updating job:', err);
      setError(err.response?.data?.message || 'Failed to update job posting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom>
        Edit Job Posting
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Job Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Experience Level</InputLabel>
                <Select
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  label="Experience Level"
                >
                  <MenuItem value="entry">Entry Level</MenuItem>
                  <MenuItem value="mid">Mid Level</MenuItem>
                  <MenuItem value="senior">Senior Level</MenuItem>
                  <MenuItem value="lead">Lead</MenuItem>
                  <MenuItem value="executive">Executive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Job Type</InputLabel>
                <Select
                  name="jobType"
                  value={formData.jobType}
                  onChange={handleChange}
                  label="Job Type"
                >
                  <MenuItem value="full-time">Full Time</MenuItem>
                  <MenuItem value="part-time">Part Time</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="internship">Internship</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Work Mode</InputLabel>
                <Select
                  name="workMode"
                  value={formData.workMode}
                  onChange={handleChange}
                  label="Work Mode"
                >
                  <MenuItem value="onsite">On-site</MenuItem>
                  <MenuItem value="remote">Remote</MenuItem>
                  <MenuItem value="hybrid">Hybrid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Number of Openings"
                name="openings"
                type="number"
                value={formData.openings}
                onChange={handleChange}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Salary (₹)"
                name="salaryMin"
                type="number"
                value={formData.salaryMin}
                onChange={handleChange}
                placeholder="Optional - Leave blank if confidential"
                InputProps={{
                  startAdornment: <Box sx={{ mr: 1 }}>₹</Box>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum Salary (₹)"
                name="salaryMax"
                type="number"
                value={formData.salaryMax}
                onChange={handleChange}
                placeholder="Optional - Leave blank if confidential"
                InputProps={{
                  startAdornment: <Box sx={{ mr: 1 }}>₹</Box>,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Application Deadline"
                  name="applicationDeadline"
                  type="date"
                  value={formData.applicationDeadline}
                  onChange={handleChange}
                  disabled={isRollingApplications}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required={!isRollingApplications}
                />
                <Button
                  variant={isRollingApplications ? "contained" : "outlined"}
                  onClick={() => setIsRollingApplications(!isRollingApplications)}
                  sx={{ minWidth: 200, height: 56 }}
                >
                  {isRollingApplications ? "Rolling Applications ✓" : "Accepting Rolling Applications"}
                </Button>
              </Box>
              {isRollingApplications && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Applications will be accepted on a rolling basis with no fixed deadline
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Key Responsibilities (one per line)"
                name="responsibilities"
                value={formData.responsibilities}
                onChange={handleChange}
                placeholder="Enter each responsibility on a new line"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Education Requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                placeholder="e.g., Bachelor's degree in Computer Science or related field"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box>
                <TextField
                  label="Add Required Skills"
                  value={currentSkill}
                  onChange={(e) => setCurrentSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSkill())}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddSkill}
                  size="medium"
                >
                  Add Skill
                </Button>
                <Box sx={{ mt: 1 }}>
                  {skills.map((skill) => (
                    <Chip
                      key={skill}
                      label={skill}
                      onDelete={() => handleDeleteSkill(skill)}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Benefits (one per line)"
                name="benefits"
                value={formData.benefits}
                onChange={handleChange}
                placeholder="Enter each benefit on a new line"
              />
            </Grid>
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<Save />}
                  disabled={saving}
                  sx={{ flex: 1 }}
                >
                  {saving ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  startIcon={<Cancel />}
                  onClick={() => navigate('/manage-jobs')}
                  sx={{ flex: 1 }}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        message="Job updated successfully!"
      />
    </Container>
  );
};

export default EditJob;
